<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0" data-testid="rollouts-page-title">
          <v-icon class="mr-2">mdi-rocket-launch</v-icon>
          Progressive Rollouts
        </h3>
        <v-spacer />
        <v-btn
          class="mr-2"
          color="info"
          :loading="loading"
          data-testid="rollout-refresh-btn"
          @click="load"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          color="success"
          data-testid="rollout-create-btn"
          @click="$router.push('/game-rollouts/new')"
        >
          Create
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          label="Search"
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          hide-details
          class="mb-4"
          placeholder="Game, feed, status, or percentage"
          data-testid="rollout-search"
        />

        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-icon left>mdi-filter</v-icon>
              Filters
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.feedName"
                    label="Feed name"
                    clearable
                    dense
                    hide-details="auto"
                    data-testid="rollout-filter-feed-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.gameName"
                    label="Game name"
                    clearable
                    dense
                    hide-details="auto"
                    data-testid="rollout-filter-game-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.gameSlug"
                    label="Game slug"
                    clearable
                    dense
                    hide-details="auto"
                    data-testid="rollout-filter-game-slug"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filters.status"
                    :items="statusFilterItems"
                    label="Status"
                    clearable
                    dense
                    hide-details="auto"
                    data-testid="rollout-filter-status"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filters.segmentation"
                    :items="segmentationFilterItems"
                    label="Segmentation"
                    clearable
                    dense
                    hide-details="auto"
                    data-testid="rollout-filter-segmentation"
                  />
                </v-col>
              </v-row>
              <div class="d-flex mt-3">
                <v-btn color="primary" data-testid="rollout-apply-filters" @click="load">Apply filters</v-btn>
                <v-btn class="ml-2" text data-testid="rollout-clear-filters" @click="clearFilters">Clear</v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mb-2">
          <v-chip color="info" small>
            Showing {{ visibleItems.length }} of {{ items.length }} rollouts
          </v-chip>
        </div>

        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="visibleItems"
          item-key="id"
        >
          <template v-slot:item.statusLabel="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>
              {{ item.statusLabel }}
            </v-chip>
          </template>
          <template v-slot:item.percentage="{ item }">
            {{ item.percentage }}%
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="rollouts-list__actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="`rollout-edit-${item.id}`"
                    @click="openEdit(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="secondary"
                    tag="a"
                    :href="changeLogHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :data-testid="`rollout-history-${item.id}`"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-history
                  </v-icon>
                </template>
                <span>View change history</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../../templates/basic.vue';
import {
  ROLLOUT_STATUSES,
  ROLLOUT_SEGMENTATIONS,
  STATUS_LABELS,
  SEGMENTATION_LABELS,
  CHANGE_LOG_TABLE,
  formatRolloutDate,
} from './constants';

export default {
  name: 'GameRolloutsList',
  components: { BasicTemplate },
  data() {
    return {
      items: [],
      search: '',
      loading: false,
      filters: {
        feedName: '',
        gameName: '',
        gameSlug: '',
        status: null,
        segmentation: null,
      },
      headers: [
        { text: 'Game', value: 'gameLabel', sortable: false },
        { text: 'Feed', value: 'feedLabel', sortable: false },
        { text: 'Status', value: 'statusLabel' },
        { text: 'Percentage %', value: 'percentage' },
        { text: 'Segmentation', value: 'segmentationLabel' },
        { text: 'Last update', value: 'updatedAtLabel' },
        { text: 'Updated by', value: 'updatedBy' },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px', align: 'center' },
      ],
    };
  },
  computed: {
    visibleItems() {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return this.items;

      return this.items.filter((item) => {
        for (const header of this.headers) {
          if (!header?.value || header.value === 'actions') continue;
          const value = item?.[header.value];
          if (value == null || typeof value === 'boolean') continue;
          if (String(value).toLowerCase().includes(query)) return true;
        }
        return false;
      });
    },
    statusFilterItems() {
      return ROLLOUT_STATUSES.map(value => ({
        value,
        text: STATUS_LABELS[value] || value,
      }));
    },
    segmentationFilterItems() {
      return ROLLOUT_SEGMENTATIONS.map(value => ({
        value,
        text: SEGMENTATION_LABELS[value] || value,
      }));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    normalizeItem(item, index) {
      const feedGameId = item.feedGameId ?? item.feedGame?.id ?? item.id ?? index;
      const status = item.status ?? 'draft';
      const segmentation = item.segmentation ?? 'all';
      return {
        id: feedGameId,
        feedGameId,
        percentage: item.percentage ?? 0,
        segmentation,
        status,
        statusLabel: STATUS_LABELS[status] || status,
        segmentationLabel: SEGMENTATION_LABELS[segmentation] || segmentation,
        updatedAt: item.updatedAt,
        updatedAtLabel: formatRolloutDate(item.updatedAt),
        updatedBy: item.updatedBy || '—',
        gameLabel: this.formatGame(item),
        feedLabel: this.formatFeed(item),
      };
    },
    formatGame(item) {
      const game = item.feedGame?.game;
      if (!game) return '—';
      return `${game.name} (${game.slug})`;
    },
    formatFeed(item) {
      return item.feedGame?.feed?.name || '—';
    },
    statusColor(status) {
      const colors = {
        draft: 'grey',
        active: 'success',
        stopped: 'warning',
        deleted: 'error',
        completed: 'info',
      };
      return colors[status] || 'grey';
    },
    changeLogLink(item) {
      const entityId = item.id ?? item.feedGameId;
      return {
        path: '/game-data-change-log',
        query: {
          table: CHANGE_LOG_TABLE,
          entityId: String(entityId),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    clearFilters() {
      this.filters = {
        feedName: '',
        gameName: '',
        gameSlug: '',
        status: null,
        segmentation: null,
      };
      this.load();
    },
    buildQueryString() {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          params.append(key, String(value).trim());
        }
      });
      const query = params.toString();
      return query ? `?${query}` : '';
    },
    async load() {
      this.loading = true;
      try {
        await this.$msal.ensureAuthenticated();
        const response = await this.$gameApi.get(`/feed-game-rollout${this.buildQueryString()}`);
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        this.items = list.map((item, index) => this.normalizeItem(item, index));
      } catch (error) {
        console.error('Error loading rollouts:', error);
        this.items = [];
        await this.$swal.fire({
          icon: 'error',
          title: 'Failed to load rollouts',
          text: error?.message || error?.details || 'Please try again.',
          timer: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    openEdit(item) {
      const id = item.id ?? item.feedGameId;
      if (!id) return;
      this.$router.push(`/game-rollouts/${id}`);
    },
  },
};
</script>

<style scoped>
.rollouts-list__actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
}

::v-deep th:last-child,
::v-deep td:last-child {
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
  white-space: nowrap;
  text-align: center;
}
</style>
